/* eslint-disable react/prop-types */
import React from 'react';
import { colors, lineColors } from '../../utils';
import { useTranslation } from 'react-i18next';
import { Icon } from '@mui/material';
import { tokens } from '../../theme/tokens';
import {
  Bar,
  BarChart,
  LineChart,
  Line,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Title, LegacyGrid as Grid } from '../../elements';
import { AnimalWeight, AnimalWeightAnalytic, AnimalWeightMeasurement } from '../../models/animal-view';
import { DISPLAY_WEIGHT_UNIT, convertWeight, getWeightUnitLabel } from '../../utils';

interface IProps {
  weight: AnimalWeight;
  weightAnalytics: AnimalWeightAnalytic[];
  showTitle?: boolean;
}

const AnimalViewWeightsAnalyticsGraph = ({ weight, weightAnalytics, showTitle = false }: IProps) => {
  const { t } = useTranslation();

  // Always kilograms - the farm's weight type is not consulted, and a single Y axis cannot mix
  // grams for the birth bar with kilograms for the slaughter bar.
  const weightUnit = DISPLAY_WEIGHT_UNIT;

  const [shouldCallSearch, setShouldCallSearch] = React.useState(true);

  const convertWeightValue = (weightData: AnimalWeightMeasurement | null | undefined): number | null => {
    if (!weightData) return null;
    return convertWeight(weightData.value, weightData.unit, weightUnit);
  };

  const renderWeightsGraph = (weight: AnimalWeight) => {
    const data = [
      { name: t('f-animal-birth'), weight: convertWeightValue(weight?.birthed) },
      { name: t('f-animal-weaned'), weight: convertWeightValue(weight?.weaned) },
      { name: t('f-animal-ten-weeks'), weight: convertWeightValue(weight?.tenWeeks) },
      { name: t('f-animal-slaughtered'), weight: convertWeightValue(weight?.slaughtered) }
    ];

    const unitLabel = getWeightUnitLabel(weightUnit, t);

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} style={CHART_FONT}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name">
            <Label offset={0} position="bottom" />
          </XAxis>
          <YAxis label={{ value: unitLabel, angle: -90, position: 'insideLeft' }} />
          <Tooltip formatter={(value: any) => `${value} ${unitLabel}`} />
          <Bar dataKey="weight" barSize={25} fill={colors.blue}>
            {/*<LabelList dataKey="name" position="top" />*/}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderWeightAnalyticsInTime = (weights: AnimalWeightAnalytic[]) => {
    // Build chart data preserving all individual data points, even multiple measurements on the same day.
    // For each point, try to merge into an existing entry at the same daysAge that doesn't already
    // have a value for this model. If none exists, create a new entry.
    const chartData: Record<string, any>[] = [];

    weights.forEach((model) => {
      model.chartData.forEach((point) => {
        const valueInGrams = point.value * 1000;
        const convertedValue = convertWeight(valueInGrams, 1, weightUnit);

        // Treat day 0 as day 1
        const displayDay = point.daysAge === 0 ? 1 : point.daysAge;

        // Find an existing entry at this daysAge that doesn't have this model's value yet
        let entry = chartData.find((e) => e.daysAge === displayDay && e[model.name] === undefined);

        if (!entry) {
          entry = { daysAge: displayDay };
          chartData.push(entry);
        }

        entry[model.name] = convertedValue;
        entry[`_meta_${model.name}`] = { totalCount: point.totalCount };
      });
    });

    chartData.sort((a, b) => a.daysAge - b.daysAge);
    const unitLabel = getWeightUnitLabel(weightUnit, t);

    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="daysAge" type="number" label={{ value: t('f-days'), position: 'insideBottom', offset: -5 }} />
          <YAxis label={{ value: unitLabel, angle: -90, position: 'insideLeft' }} />

          <Tooltip
            formatter={(value: any, name: any, props: any) => {
              const meta = props.payload[`_meta_${name}`];
              const count = meta?.totalCount ?? '-';
              return [`${value} ${unitLabel}`, `${name} (${count} ${t('f-total-records')})`];
            }}
            labelFormatter={(label) => `${t('f-day')}: ${label}`}
          />

          {weights.map((model, index) => (
            <Line
              key={model.name}
              type="monotone"
              dataKey={model.name}
              stroke={lineColors[index % lineColors.length]}
              strokeWidth={2}
              dot={true}
              connectNulls={true}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  React.useEffect(() => {
    if (shouldCallSearch === true) {
      setShouldCallSearch(false);
    }
  }, [shouldCallSearch]);

  return (
    <>
      <Grid item container justifyContent="space-between" alignItems="center" spacing={2}>
        <Grid item>
          {showTitle && (
            <Grid container direction="row" spacing={1} alignItems="center">
              <Grid item>
                <Icon color="primary">scale</Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-animal-weight-title')}</Title>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12}>
          <div className="h-[200px] w-full">{renderWeightsGraph(weight)}</div>
        </Grid>
      </Grid>

      {weightAnalytics && weightAnalytics.length > 0 && (
        <Grid container>
          <Grid item xs={12}>
            <div className="h-[200px] w-full">{renderWeightAnalyticsInTime(weightAnalytics)}</div>
          </Grid>
        </Grid>
      )}

      {weight?.weightPerDay ? (
        <Grid container>
          <Grid item>
            <Grid container direction="row" spacing={1} alignItems="center">
              <Grid item>
                <Title>
                  {t('f-animal-daily-grow')}:{' '}
                  {(convertWeight(weight?.weightPerDay || 0, 1, weightUnit) || 0).toFixed(3)} (
                  {getWeightUnitLabel(weightUnit, t)})
                </Title>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : null}
    </>
  );
};

export default AnimalViewWeightsAnalyticsGraph;

const CHART_FONT = { fontSize: '0.9rem', fontFamily: tokens.fontFamily };
